import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { api } from "@/lib/api";
import {
  ShieldCheck, Copy, Mail, QrCode, CheckCircle2, Lock, Link2, Clock, AlertTriangle,
  Download, Fingerprint, RefreshCw, ChevronDown, MailCheck, Users,
} from "lucide-react";

const STATUS_LABELS = {
  self_declared: { label: "Self-Declared", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  awaiting_witness: { label: "Awaiting Witness", cls: "bg-amber-50 text-amber-800 border-amber-300" },
  witnessed: { label: "Witnessed", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  mutually_witnessed: { label: "\u{1F517} Mutually Witnessed", cls: "bg-blue-50 text-blue-800 border-blue-300" },
  witness_required_again: { label: "Witness Required Again", cls: "bg-rose-50 text-rose-800 border-rose-300" },
};

const STEPS = ["Declare", "Verify Email", "Sign", "Get Witnessed", "Verify"];

const stepState = (view) => {
  // Declare + Sign completed at creation; Verify Email gates the middle;
  // Get Witnessed completes on witnessing; Verify is always available after that.
  if (["witnessed", "mutually_witnessed"].includes(view.witness_status)) return 4;
  if (!view.email_verified) return 1;
  return 3;
};

export default function MyDeclaration() {
  const { ownerToken } = useParams();
  const [view, setView] = useState(null);
  const [error, setError] = useState(null);
  const [edit, setEdit] = useState({ voted_date: "", vote_method: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/public/my-declaration/${ownerToken}`)
      .then((r) => setView(r.data))
      .catch(() => setError("This declaration link was not found."));
  }, [ownerToken]);

  useEffect(() => { load(); }, [load]);

  const copyLink = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch {
      toast.error("Could not copy — select and copy manually");
    }
  };

  const [resendBusy, setResendBusy] = useState(false);

  const resendVerification = async () => {
    setResendBusy(true);
    try {
      const r = await api.post(`/public/identity/resend/${ownerToken}`);
      toast.success(r.data.detail || "Verification email sent");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resend right now");
    } finally {
      setResendBusy(false);
    }
  };

  const requestWitness = async () => {
    try {
      const r = await api.post(`/public/witness-network/request/${ownerToken}`);
      toast.success(r.data.detail || "Witness request created");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create a witness request");
    }
  };

  const cancelWitnessRequest = async () => {
    try {
      await api.post(`/public/witness-network/request/${ownerToken}/cancel`);
      toast.success("Witness request cancelled");
      load();
    } catch {
      toast.error("Could not cancel the request");
    }
  };

  const regenerate = async () => {
    try {
      await api.post(`/public/my-declaration/${ownerToken}/regenerate-witness-link`);
      toast.success("New witness link created — the old link no longer works");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create a new link");
    }
  };

  const saveEdit = async () => {
    const payload = {};
    if (edit.voted_date) payload.voted_date = edit.voted_date;
    if (edit.vote_method) payload.vote_method = edit.vote_method;
    if (!Object.keys(payload).length) return;
    setBusy(true);
    try {
      const r = await api.post(`/public/my-declaration/${ownerToken}/update`, payload);
      if (r.data.witness_invalidated) {
        toast.warning("Declaration updated — your previous witness signature is no longer valid. Please get witnessed again.");
      } else {
        toast.success("Declaration updated");
      }
      setEdit({ voted_date: "", vote_method: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadReceipt = async () => {
    try {
      const r = await api.get(`/public/my-declaration/${ownerToken}/receipt`);
      const d = r.data;
      const html = `<!doctype html><html><head><meta charset="utf-8"><title>Witness Your Vote Receipt — ${d.declaration_number}</title>
<style>body{font-family:Georgia,serif;max-width:640px;margin:40px auto;color:#111;padding:0 16px}h1{font-size:22px}.box{border:1px solid #d1d5db;border-radius:8px;padding:14px 16px;margin:12px 0}.mono{font-family:monospace;word-break:break-all}.small{font-size:12px;color:#555}.ok{color:#047857}.test{display:inline-block;border:2px solid #b45309;color:#b45309;padding:2px 10px;font-weight:bold;border-radius:6px}</style></head><body>
<h1>WITNESS YOUR VOTE RECEIPT</h1>
${d.is_test ? '<p class="test">TEST DECLARATION</p>' : ""}
<p>${d.summary.election || ""}</p>
<div class="box"><strong>Declaration:</strong> <span class="mono">${d.declaration_number}</span><br>
Municipality: ${d.summary.municipality || ""}<br>Ward: ${d.summary.ward || "—"}</div>
<div class="box">
<p class="ok">\u2705 Voter declaration signed — ${d.summary.signed_at}</p>
${d.attestations.filter((a) => a.status === "active").map((a) => `<p class="ok">\u2705 Witness attestation signed — ${a.witness_name} — ${a.signed_at}</p>`).join("")}
<p>\u{1F517} Mutual Witness: ${d.mutual_witness ? "Yes" : "No"}</p>
<p>\u{1F512} Record locked: ${d.locked ? "Yes" : "No"}</p>
<p>\u{1F510} Integrity record created: ${d.integrity_hash ? "Yes" : "Pending witness"}</p>
</div>
<div class="box"><strong>Integrity reference</strong><br><span class="mono">${d.integrity_reference || d.record_integrity_hash?.slice(0, 16) || ""}</span></div>
<div class="box" style="display:flex;gap:16px;align-items:center">
<img src="${d.qr_url}" width="120" height="120" alt="QR verification code" style="border:1px solid #d1d5db;border-radius:6px"/>
<div><strong>Verify this record</strong><br><span class="small">Scan the QR code or visit:</span><br><span class="mono" style="font-size:12px">${d.verify_url}</span></div>
</div>
<p class="small">Created: ${new Date().toLocaleString("en-CA")}</p>
<p class="small">${d.disclaimer}</p>
</body></html>`;
      const blob = new Blob([html], { type: "text/html" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `witness-your-vote-receipt-${d.declaration_number}.html`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Receipt downloaded");
    } catch {
      toast.error("Could not build the receipt");
    }
  };

  if (error) {
    return (
      <div className="wyv-container py-16">
        <Card className="max-w-lg"><CardContent className="p-6 text-sm text-muted-foreground flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> {error}
        </CardContent></Card>
      </div>
    );
  }
  if (!view) {
    return <div className="wyv-container py-16 max-w-2xl space-y-4"><Skeleton className="h-24" /><Skeleton className="h-64" /></div>;
  }

  const st = STATUS_LABELS[view.witness_status] || STATUS_LABELS.self_declared;
  const active = stepState(view);

  return (
    <div className="wyv-container py-10 sm:py-14 max-w-3xl">
      {/* 4-step progress */}
      <ol className="flex items-center gap-1 sm:gap-2 mb-8" aria-label="Progress" data-testid="witness-progress">
        {STEPS.map((s, i) => (
          <li key={s} className="flex items-center gap-1 sm:gap-2 flex-1">
            <span className={`h-8 w-8 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${
              i < active ? "bg-accent text-white" : i === active ? "bg-primary text-white" : "bg-slate-200 text-slate-500"}`}>
              {i < active ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
            </span>
            <span className={`text-[11px] sm:text-xs font-semibold ${i <= active ? "text-foreground" : "text-muted-foreground"}`}>{s}</span>
            {i < STEPS.length - 1 && <span className="hidden sm:block flex-1 h-px bg-slate-200" />}
          </li>
        ))}
      </ol>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground" data-testid="my-declaration-title">
            Your Declaration Has Been Created
          </h1>
          <p className="font-mono text-sm text-muted-foreground mt-1" data-testid="my-declaration-number">
            Declaration ID: {view.declaration_number}
          </p>
        </div>
        <Badge variant="outline" className={`${st.cls} text-sm px-3 py-1`} data-testid="witness-status-badge">{st.label}</Badge>
      </div>

      {view.is_test && (
        <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 mt-3">TEST DECLARATION</Badge>
      )}

      {/* Declaration summary */}
      <Card className="mt-6">
        <CardContent className="p-6">
          <h2 className="font-heading font-semibold text-sm text-foreground mb-3">Your declaration</h2>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {[
              ["Municipality", view.summary.municipality],
              ["Ward", view.summary.ward || "—"],
              ["Election", view.summary.election],
              ["Voting date", view.summary.voted_date],
              ["Voting method", view.summary.vote_method?.replace(/_/g, " ")],
              ["Declared for", view.summary.candidate],
              ["Signature", view.summary.signature_type === "typed" ? "Electronic (typed)" : "Electronic (drawn)"],
              ["Signed", view.summary.signed_at ? new Date(view.summary.signed_at).toLocaleString("en-CA") : "—"],
            ].map(([k, v]) => (
              <p key={k} className="flex justify-between gap-3 border-b border-slate-100 py-1.5">
                <span className="text-muted-foreground text-xs">{k}</span>
                <span className="font-medium text-right" data-testid={`summary-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v}</span>
              </p>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Witness tools when a witness is needed */}
      {view.witness_request && !["witnessed", "mutually_witnessed"].includes(view.witness_status) && (
        <Card className="mt-5 border-amber-200" data-testid="witness-tools-card">
          <CardContent className="p-6">
            <h2 className="font-heading font-semibold text-base text-foreground mb-1">
              {view.witness_status === "witness_required_again" ? "Your declaration needs a new witness" : "Get your declaration witnessed"}
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Choose someone you trust. Send them your witness link or show them the QR code.
            </p>
            <div className="rounded-lg bg-secondary border border-slate-200 p-4 mt-4 text-xs text-muted-foreground leading-relaxed" data-testid="ballot-privacy-note">
              {view.ballot_privacy_note}
            </div>
            <div className="mt-5 grid sm:grid-cols-[auto_1fr] gap-5 items-start">
              <img
                src={view.witness_request.qr_url}
                alt="QR code — opens your private witness link"
                className="h-32 w-32 rounded-lg border border-slate-300 bg-white"
                data-testid="witness-qr-image"
              />
              <div className="space-y-3 min-w-0">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-0.5">Witness Code</p>
                  <p className="font-mono font-bold text-2xl text-foreground" data-testid="witness-code">{view.witness_request.witness_code}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Private witness link</p>
                  <p className="font-mono text-[11px] text-muted-foreground break-all" data-testid="witness-link-text">{view.witness_request.witness_url}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => copyLink(view.witness_request.witness_url)} data-testid="copy-witness-link">
                    <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy Link
                  </Button>
                  <Button size="sm" variant="outline" asChild data-testid="email-witness-link">
                    <a href={`mailto:?subject=${encodeURIComponent("Please witness my WitnessYourVote declaration")}&body=${encodeURIComponent(`Hi,\n\nPlease witness my WitnessYourVote declaration. You are confirming that I made and signed my declaration — you do not need to see my ballot.\n\nOpen this link:\n${view.witness_request.witness_url}\n\nThank you!`)}`}>
                      <Mail className="h-3.5 w-3.5 mr-1.5" /> Email Witness Link
                    </a>
                  </Button>
                  <Button size="sm" variant="ghost" onClick={regenerate} data-testid="regenerate-witness-link">
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> New link
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Link expires {new Date(view.witness_request.expires_at).toLocaleDateString("en-CA")} and stops working after it is used.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mutual witness opportunities */}
      {view.mutual_opportunities?.length > 0 && (
        <Card className="mt-5 border-blue-200 bg-blue-50/50" data-testid="mutual-opportunity-card">
          <CardContent className="p-6">
            <h2 className="font-heading font-semibold text-base text-foreground mb-1 inline-flex items-center gap-2">
              <Link2 className="h-4 w-4 text-primary" /> Return the Witness
            </h2>
            {view.mutual_opportunities.map((o, i) => (
              <div key={i} className="mt-3">
                <p className="text-sm text-foreground">
                  <span className="font-semibold">{o.witness_name}</span> has witnessed your declaration.{" "}
                  {o.witness_name?.split(" ")[0]} also has a declaration awaiting a witness.
                </p>
                <Button asChild className="mt-3 bg-accent hover:bg-accent/90" data-testid={`witness-back-${i}`}>
                  <a href={o.witness_url}>WITNESS {o.witness_name?.split(" ")[0]?.toUpperCase()}'S DECLARATION</a>
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Email verification — production flow (check your email + resend) */}
      {!view.email_verified && (
        <Card className="mt-5 border-blue-200" data-testid="email-verify-card">
          <CardContent className="p-5">
            <h2 className="font-heading font-semibold text-sm text-foreground mb-1 inline-flex items-center gap-2">
              <MailCheck className="h-4 w-4 text-accent" /> Check your email
            </h2>
            <p className="text-sm text-foreground mt-1">
              We sent a verification link to:{" "}
              <span className="font-mono font-semibold" data-testid="masked-email">{view.identity?.email_masked}</span>
            </p>
            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
              Verifying your email unlocks Mutual Witness status and the “Find Me a Witness” service.
              The link works once and expires after 48 hours.
            </p>
            <div className="flex items-center gap-3 mt-3">
              <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 text-[10px]" data-testid="verification-status-badge">
                {view.identity?.verification_status === "sent" ? "Verification Sent"
                  : view.identity?.verification_status === "expired" ? "Verification Expired"
                  : view.identity?.verification_status === "failed" ? "Verification Failed" : "Unverified"}
              </Badge>
              <Button size="sm" variant="outline" onClick={resendVerification} disabled={resendBusy} data-testid="resend-verification-button">
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> {resendBusy ? "Sending…" : "RESEND VERIFICATION EMAIL"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Witness Network — I NEED A WITNESS */}
      {!["witnessed", "mutually_witnessed"].includes(view.witness_status) && (
        <Card className="mt-5" data-testid="witness-network-card">
          <CardContent className="p-5">
            <h2 className="font-heading font-semibold text-sm text-foreground mb-1 inline-flex items-center gap-2">
              <Users className="h-4 w-4 text-accent" /> Don't have a witness?
            </h2>
            {view.witness_network_request ? (
              <div className="mt-2" data-testid="witness-request-status">
                <p className="text-sm text-foreground">
                  Your witness request is{" "}
                  <span className="font-semibold">
                    {{ open: "in the queue", matching: "in the queue", match_offered: "offered to a participating witness",
                       accepted: "accepted — your witness will sign soon" }[view.witness_network_request.request_status] || view.witness_network_request.request_status}
                  </span>.
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Requested {new Date(view.witness_network_request.created_at).toLocaleDateString("en-CA")} · expires{" "}
                  {new Date(view.witness_network_request.expires_at).toLocaleDateString("en-CA")}
                </p>
                <Button size="sm" variant="ghost" className="mt-2 text-rose-700 hover:text-rose-800 hover:bg-rose-50"
                        onClick={cancelWitnessRequest} data-testid="cancel-witness-request">
                  Cancel request
                </Button>
              </div>
            ) : view.email_verified ? (
              <>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Ask WitnessYourVote to match you with a participating witness in your municipality.
                  Witnessing is completed digitally — no personal contact details are exchanged.
                </p>
                <Button size="sm" className="mt-3 bg-accent hover:bg-accent/90" onClick={requestWitness} data-testid="find-me-a-witness-button">
                  <Users className="h-3.5 w-3.5 mr-1.5" /> FIND ME A WITNESS
                </Button>
              </>
            ) : (
              <p className="text-xs text-muted-foreground" data-testid="witness-network-verify-first">
                Verify your email above to use the “Find Me a Witness” service.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {view.mutual_pending_email_verification && (
        <Card className="mt-5 border-amber-200 bg-amber-50">
          <CardContent className="p-4 text-sm text-amber-900" data-testid="mutual-pending-note">
            <span className="font-semibold">Witnessed — Email Verification Required.</span> You and your witness have
            witnessed each other — Mutual Witness status is granted once both of you have confirmed your email addresses.
          </CardContent>
        </Card>
      )}

      {/* Status timeline */}
      <Card className="mt-5" data-testid="witness-timeline">
        <CardContent className="p-6">
          <h2 className="font-heading font-semibold text-sm text-foreground mb-3">Record status</h2>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2 text-foreground"><CheckCircle2 className="h-4 w-4 text-accent" /> Voter declaration signed</li>
            {view.attestations.map((a, i) => (
              <li key={i} className={`flex items-center gap-2 ${a.status === "active" ? "text-foreground" : "text-muted-foreground line-through"}`}>
                {a.status === "active" ? <CheckCircle2 className="h-4 w-4 text-accent" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />}
                Witness attestation signed — {a.witness_name}
                {a.status !== "active" && <span className="text-[10px] no-underline">(no longer valid — declaration changed)</span>}
              </li>
            ))}
            {view.attestations.length === 0 && (
              <li className="flex items-center gap-2 text-muted-foreground"><Clock className="h-4 w-4" /> Witness attestation — waiting</li>
            )}
            <li className={`flex items-center gap-2 ${view.locked ? "text-foreground" : "text-muted-foreground"}`}>
              <Lock className="h-4 w-4" /> {view.locked ? "Record locked and integrity protected" : "Record locks after witnessing"}
            </li>
            {view.witness_status === "mutually_witnessed" && (
              <li className="flex items-center gap-2 text-blue-800 font-semibold" data-testid="mutual-witnessed-row">
                <Link2 className="h-4 w-4" /> Mutually Witnessed{view.mutual_partners.length ? ` — with ${view.mutual_partners.join(", ")}` : ""}
              </li>
            )}
          </ul>
        </CardContent>
      </Card>

      {/* Receipt + verify */}
      <div className="flex flex-wrap gap-3 mt-6">
        <Button onClick={downloadReceipt} data-testid="download-witness-receipt">
          <Download className="h-4 w-4 mr-1.5" /> Download Receipt
        </Button>
        <Button asChild variant="outline" data-testid="open-receipt-card">
          <Link to={`/receipt-card/${ownerToken}`}><QrCode className="h-4 w-4 mr-1.5" /> Wallet Receipt Card</Link>
        </Button>
        <Button asChild variant="outline" data-testid="open-verify-page">
          <Link to={`/verify/${view.public_verification_id}`}><Fingerprint className="h-4 w-4 mr-1.5" /> Open Verification Page</Link>
        </Button>
      </div>

      {/* Correct declaration (collapsed — warns about re-witnessing) */}
      <Collapsible className="mt-8">
        <CollapsibleTrigger className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 inline-flex items-center gap-1" data-testid="edit-declaration-toggle">
          Need to correct your declaration? <ChevronDown className="h-3 w-3" />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Card className="mt-3 border-rose-200">
            <CardContent className="p-5">
              <p className="text-xs text-rose-800 mb-4">
                Changing your voting date or method after witnessing keeps the old version on file, cancels the
                existing witness signature, and your declaration must be witnessed again.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="edit-date" className="text-xs">Voting date</Label>
                  <Input id="edit-date" type="date" className="mt-1" value={edit.voted_date}
                         onChange={(e) => setEdit({ ...edit, voted_date: e.target.value })} data-testid="edit-voted-date" />
                </div>
                <div>
                  <Label className="text-xs">Voting method</Label>
                  <Select value={edit.vote_method || undefined} onValueChange={(v) => setEdit({ ...edit, vote_method: v })}>
                    <SelectTrigger className="mt-1 bg-white" data-testid="edit-vote-method"><SelectValue placeholder="No change" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="telephone">Telephone</SelectItem>
                      <SelectItem value="other_authorized">Other authorized method</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button size="sm" variant="outline" className="mt-4" onClick={saveEdit} disabled={busy} data-testid="save-edit-button">
                Save correction
              </Button>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>

      <p className="text-[11px] text-muted-foreground leading-relaxed mt-8" data-testid="my-declaration-disclaimer">
        {view.disclaimer}
      </p>
    </div>
  );
}
